// Flag director_shows_work: when a Director tool changes something, the app shows where (the chat works like a
// tutorial for the real UI). Two halves:
//   workChip()  pure: the tool, its params and its `ui` -> an "action chip" (icon, short past-tense label, where it
//               lives), or null for a read (how_do_i, list_*, ...) or a result that changed nothing. Unit-tested.
//   showWork()  the DOM side: open the panel it lives in (the suite's tab / fold, a scene) and flash the control,
//               reusing highlight.ts's flash and fold lookup. Automatic after a tool only on the page already open,
//               never while the user is typing or reading the chat sheet; the chip's "Show" does it on demand.
// Targets come from WORK below first (the panel the result lands in), then parity.ts's map of the taps that do the
// same thing (tapsFor: their data-action ids), so a new tap with a parity line is found without touching this file.
import { parseUi } from "./directives";
import { flagOn } from "./flags";
import { setFold } from "./fold";
import { findTarget, flash } from "./highlight";
import { navigate } from "./router";
import type { UiDirective } from "./types";

export const SHOWS_WORK_FLAG = "director_shows_work";
/** Closes the phone's chat sheet so a "Show" can be seen (components/Director.tsx listens with the flag on). */
export const CLOSE_CHAT_EVENT = "cao:close-chat";

export type WorkChip = {
  tool: string;
  label: string;
  icon: string;
  /** the page it lives on (null: wherever the user is) */
  path: string | null;
  /** the scene it's about, 1-based (a ?scene= opens it on the video screen) */
  scene: number | null;
  /** a section to open first: a suite tab or fold name (suitefolds.ts), or a fold id */
  place: string | null;
  /** an exact fold to open after the place */
  fold: string | null;
  /** CSS selectors to flash, best first (trusted: written here, never from a tool) */
  targets: string[];
};

type Work = { label: string; icon: string; place?: string; fold?: string; targets?: string[]; scene?: boolean; path?: string };

const STRIP = ".strip";
const WORLD = "#world";
const BRIEF = "#brief-h";
const scenes = (label: string, icon = "edit"): Work => ({ label, icon, targets: [STRIP], scene: true });

/** The tools that act, with what the chip says and where the result lives. A tool not here gets no chip. */
export const WORK: Record<string, Work> = {
  // script and scenes
  write_script: { label: "Wrote the script", icon: "script", targets: [STRIP] },
  rewrite_script: { label: "Rewrote the script", icon: "script", targets: [STRIP] },
  edit_scene: scenes("Changed the scene"),
  add_scene: scenes("Added a scene", "plus"),
  split_scene: scenes("Split the scene"),
  merge_scenes: scenes("Joined the scenes"),
  delete_scene: { label: "Removed the scene", icon: "trash", targets: [STRIP] },
  reorder_scenes: { label: "Moved the scenes", icon: "edit", targets: [STRIP] },
  scenes: scenes("Changed the scenes"),
  move_line: scenes("Moved the line"),
  // shots
  produce: { label: "Started making the shots", icon: "bolt", targets: [STRIP, ".produce"] },
  make_frame: { label: "Started the frame", icon: "image", targets: [STRIP], scene: true },
  make_clip: { label: "Started the clip", icon: "film", targets: [STRIP], scene: true },
  make_sounds: { label: "Started the sounds", icon: "wave", targets: [".tl", STRIP], scene: true },
  approve: { label: "Approved it", icon: "check", targets: [STRIP], scene: true },
  redo: { label: "Started a redo", icon: "redo", targets: [STRIP], scene: true },
  scratch: { label: "Scratched that take", icon: "trash", targets: [STRIP], scene: true },
  retry_job: { label: "Tried it again", icon: "redo", targets: [STRIP] },
  cancel_job: { label: "Stopped it", icon: "stop", targets: [STRIP] },
  set_line_speed: { label: "Changed the timing", icon: "sliders", targets: [".tl"], scene: true },
  set_model: { label: "Changed the model", icon: "sliders", place: "scene", fold: "scene.model", scene: true },
  set_lip_sync: { label: "Changed the lip sync", icon: "sliders", place: "scene", fold: "scene.model", scene: true },
  set_lip_lead: { label: "Changed the lip sync", icon: "sliders", place: "scene", fold: "scene.model", scene: true },
  lip_sync: { label: "Changed the lip sync", icon: "sliders", place: "scene", fold: "scene.model", scene: true },
  set_references: { label: "Changed the references", icon: "image", place: "scene", fold: "scene.references", scene: true },
  use_last_frame: { label: "Used the clip's last frame", icon: "image", place: "scene", fold: "scene.references", scene: true },
  use_comparison: { label: "Used that one", icon: "check", targets: [STRIP], scene: true },
  record_voices: { label: "Started the voices", icon: "mic", targets: [".tl"] },
  make_music: { label: "Started the music", icon: "music", targets: [".tl"] },
  render: { label: "Started the render", icon: "film", targets: [".render-live", ".render-btn"] },
  // brief, voices, look
  update_brief: { label: "Changed the brief", icon: "edit", place: "brief", targets: [BRIEF] },
  change_length: { label: "Changed the length", icon: "edit", place: "brief", targets: [BRIEF] },
  set_narrator: { label: "Changed the voice", icon: "mic", place: "brief", targets: ['[data-help="voices"]', BRIEF] },
  restyle: { label: "Changed the look", icon: "brand", place: "brief", fold: "brief.look" },
  set_title_card: { label: "Changed the title", icon: "edit", place: "brief", fold: "brief.title" },
  set_captions: { label: "Changed the captions", icon: "edit", place: "brief", fold: "brief.captions" },
  set_transitions: { label: "Changed the transitions", icon: "film", place: "brief", fold: "brief.transitions" },
  add_product_photo: { label: "Added the photo", icon: "image", place: "brief", fold: "brief.photos" },
  suno_pack: { label: "Wrote the Suno pack", icon: "music", place: "brief", targets: ["#song-h"] },
  // the World: characters, places, props
  set_character: { label: "Changed the cast", icon: "person", place: "cast", targets: ["#cast-h", WORLD] },
  make_character_sheet: { label: "Started the sheet", icon: "person", place: "cast", targets: ["#cast-h", WORLD] },
  approve_sheet: { label: "Approved the sheet", icon: "check", place: "cast", targets: ["#cast-h", WORLD] },
  redo_sheet: { label: "Started a redo of the sheet", icon: "redo", place: "cast", targets: ["#cast-h", WORLD] },
  sheet: { label: "Changed the sheet", icon: "person", place: "cast", targets: ["#cast-h", WORLD] },
  update_older_looks: { label: "Updated the older looks", icon: "redo", targets: [STRIP] },
  make_location: { label: "Started drawing it", icon: "image", place: "places", targets: [WORLD] },
  // versions and history
  restore_version: { label: "Restored that version", icon: "restore", place: "versions" },
  undo_change: { label: "Put it back", icon: "restore", place: "project.history" },
  changes: { label: "Changed the history", icon: "restore", place: "project.history" },
  share_link: { label: "Made a link", icon: "copy", place: "versions", targets: ['[data-help="version.share"]'] },
  // library and account (Show goes there; never by itself)
  use_from_library: { label: "Used it from your library", icon: "library", targets: [STRIP] },
  save_to_library: { label: "Saved it to your library", icon: "library", path: "/library" },
  retag: { label: "Tagged it", icon: "library", path: "/library" },
  move_item: { label: "Moved it", icon: "library", path: "/library" },
  library_item: { label: "Changed it in your library", icon: "library", path: "/library" },
  set_brand: { label: "Saved your channel", icon: "brand", targets: ['[data-help="nav.channel"]'] },
  remember: { label: "Made a note", icon: "edit", path: "/settings", targets: ['[data-help="settings.memory"]'] },
  forget: { label: "Forgot that", icon: "trash", path: "/settings", targets: ['[data-help="settings.memory"]'] },
  memory: { label: "Changed my notes", icon: "edit", path: "/settings", targets: ['[data-help="settings.memory"]'] },
  set_limit: { label: "Changed your limit", icon: "sliders", path: "/settings", targets: ['[data-help="settings.spending"]'] },
  // new things (their ui opens them)
  new_video: { label: "Started a new video", icon: "plus" },
  new_ad: { label: "Started a new ad", icon: "plus" },
  duplicate_video: { label: "Made a copy", icon: "copy" },
  new_collection: { label: "Made the collection", icon: "stack" },
  update_collection: { label: "Saved the collection", icon: "stack" },
  move_video: { label: "Moved the video", icon: "stack" },
  make_series: { label: "Made a series", icon: "stack" },
};

const CHANGED = new Set(["refresh", "show_artifact", "memory_changed", "needs_review", "open_project"]);

/** Did the result change something? Its `ui` says so: a refresh, a card, a memory change, a video opened. */
export function changedSomething(ui: UiDirective | undefined | null): boolean {
  return parseUi(ui).some((p) => (p.kind === "flat" ? !!p.d.refresh?.length : CHANGED.has(p.d.type) || (p.d.type === "navigate" && p.d.to === "project")));
}

/** The video a result is about: the tool's project_id, else the one its ui names, else the one open. */
function projectOf(params: Record<string, unknown>, ui: UiDirective | undefined | null, open: string | null): string | null {
  if (typeof params.project_id === "string" && params.project_id) return params.project_id;
  for (const p of parseUi(ui)) {
    if (p.kind !== "typed") continue;
    const d = p.d as { type: string; projectId?: unknown };
    if ((d.type === "open_project" || d.type === "refresh" || d.type === "navigate") && typeof d.projectId === "string" && d.projectId) return d.projectId;
  }
  return open;
}

const sceneOf = (v: unknown): number | null => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isInteger(n) && n > 0 && n < 1000 ? n : null;
};

/** The chip for a tool's result, or null (a read, an unknown tool, or nothing changed). `open`: the video on screen. */
export function workChip(tool: string, params: Record<string, unknown> | null | undefined, ui: UiDirective | undefined | null, open: string | null = null): WorkChip | null {
  const w = Object.prototype.hasOwnProperty.call(WORK, tool) ? WORK[tool] : undefined;
  if (!w || !changedSomething(ui)) return null;
  const args = params ?? {};
  const scene = w.scene ? sceneOf(args.scene) : null;
  const pid = w.path ? null : projectOf(args, ui, open);
  const path = w.path ?? (pid ? `/p/${encodeURIComponent(pid)}` : null);
  // the cell's <li>: React rewrites the button's own class on every progress tick, which would cut the flash short
  const cell = scene ? [`.strip > li:nth-child(${scene})`] : [];
  return { tool, label: w.label, icon: w.icon, path, scene, place: w.place ?? null, fold: w.fold ?? null, targets: [...(w.fold ? [`[data-fold="${w.fold}"]`] : []), ...cell, ...(w.targets ?? [])] };
}

/** The selectors for the taps that do what `tool` does (parity.ts tapsFor), after the chip's own. */
export function withTapTargets(chip: Pick<WorkChip, "targets">, tapIds: readonly string[]): string[] {
  const safe = tapIds.filter((id) => /^[a-z0-9][a-z0-9._-]{0,63}$/.test(id));
  return [...chip.targets, ...safe.map((id) => `[data-action="${id}"]`), ...safe.map((id) => `[data-help="${id}"]`)];
}

// ------------------------------------------------------------------ the DOM side

const typing = () => {
  const el = document.activeElement as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
};
const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const pathOnly = (p: string) => new URL(p, location.origin).pathname;

/** Puts ?scene=N on the open video's address without a scroll; the project screen picks the scene and drops it. */
function openScene(n: number) {
  const url = new URL(location.href);
  url.searchParams.set("scene", String(n));
  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

let tapIds: ((tool: string) => string[]) | null = null;
async function tapsOf(tool: string): Promise<string[]> {
  if (!tapIds) {
    try {
      tapIds = (await import("./parity")).tapsFor;
    } catch {
      tapIds = () => [];
    }
  }
  return tapIds(tool);
}

/**
 * Show where a chip's result lives. `auto` (right after the tool): only on the page already open, and not while the
 * user types or has the chat sheet up. On demand (the chip's Show): goes there first and closes the chat sheet.
 */
export async function showWork(chip: WorkChip, { auto = false, tries = 14, everyMs = 150 }: { auto?: boolean; tries?: number; everyMs?: number } = {}): Promise<boolean> {
  const here = !chip.path || pathOnly(chip.path) === location.pathname;
  if (auto && (!here || typing() || document.querySelector(".chat-sheet"))) return false;
  if (!auto) {
    window.dispatchEvent(new CustomEvent(CLOSE_CHAT_EVENT));
    if (!here && chip.path) navigate(chip.path);
  }
  const onVideo = /^\/p\/[^/]+\/?$/.test(chip.path ? pathOnly(chip.path) : location.pathname);
  if (chip.scene && onVideo) window.setTimeout(() => openScene(chip.scene!), here ? 0 : 250);
  if (chip.place) setFold(chip.place, true);
  if (chip.fold) window.setTimeout(() => setFold(chip.fold!, true), 120);
  const selectors = withTapTargets(chip, await tapsOf(chip.tool));
  const smooth = !reducedMotion();
  for (let i = 0; i < tries; i++) {
    await new Promise((r) => window.setTimeout(r, i ? everyMs : 200));
    for (const sel of selectors) {
      const el = findTarget(document, sel);
      if (el) {
        flash(el, smooth);
        return true;
      }
    }
  }
  return false;
}

/** After a Director tool (director.ts runTool): with the flag on, the chip in the chat, then the automatic show. */
export function noteWork(tool: string, params: Record<string, unknown> | null | undefined, ui: UiDirective | undefined | null, add: (chip: WorkChip) => void) {
  if (!flagOn(SHOWS_WORK_FLAG)) return;
  const open = location.pathname.match(/^\/p\/([^/]+)/)?.[1] ?? null;
  const chip = workChip(tool, params, ui, open ? decodeURIComponent(open) : null);
  if (!chip) return;
  add(chip);
  // after the ui directive's own navigation has had a moment to land
  window.setTimeout(() => void showWork(chip, { auto: true }), 350);
}
